import type {ResultadoDeEntradaABodega} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useEntradaABodega, useFiltrosDeInventario} from '../api/inventario';
import {tr} from '../i18n/idioma';
import {fecha as formatoFecha, numero} from '../utilidades/formato';
import {AYUDA_ENTRADA_A_BODEGA} from './ayuda-inventario';
import {NotaDeAyuda} from './nota-de-ayuda';

// Llena la fecha de ingreso a bodega de todos los Big Bags de una
// recoleccion. Primero revisa (cuantos cambian, que fechas tienen hoy y la
// descarga del viaje de camion como sugerencia) y despues aplica.
export function EntradaABodega({recoleccionInicial, onCerrar}: {recoleccionInicial: string; onCerrar: () => void}) {
  const accion = useEntradaABodega();
  const recolecciones = useFiltrosDeInventario({estado: 'todos'}).data?.recolecciones ?? [];
  const [recoleccion, setRecoleccion] = useState(recoleccionInicial);
  const [fecha, setFecha] = useState('');
  const [alcance, setAlcance] = useState<'vacias' | 'todas'>('vacias');
  const [revision, setRevision] = useState<ResultadoDeEntradaABodega | null>(null);
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);

  const pedir = async (modo: 'revisar' | 'aplicar', conFecha = fecha) => {
    setMensaje(null);
    try {
      const r = await accion.mutateAsync({modo, recoleccion: recoleccion.trim(), fecha: conFecha || null, alcance});
      setRevision(r);
      if (modo === 'revisar' && !conFecha && r.sugerida) setFecha(r.sugerida);
      if (modo === 'aplicar')
        setMensaje({
          texto: tr(
            `Listo: ${numero(r.aplicados)} Big Bags de ${r.recoleccion} quedaron con ingreso a bodega el ${formatoFecha(r.fecha)}.`,
            `Done: ${numero(r.aplicados)} Big Bags of ${r.recoleccion} now have warehouse entry on ${formatoFecha(r.fecha)}.`,
          ),
          error: false,
        });
    } catch (e) {
      setRevision(null);
      setMensaje({
        texto: e instanceof ErrorDeApi ? e.message : tr('No se pudo consultar.', 'Could not load.'),
        error: true,
      });
    }
  };

  // Abierta desde una fila: revisa de una vez para mostrar la sugerencia.
  useEffect(() => {
    if (recoleccionInicial) void pedir('revisar', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoleccionInicial]);

  const vigente = revision && revision.recoleccion.toUpperCase() === recoleccion.trim().toUpperCase();
  const aCambiar =
    vigente && revision.modo === 'revisar' && revision.fecha === (fecha || null) ? revision.aCambiar : null;

  return (
    <div
      role="region"
      aria-label={tr('Ingreso a bodega por recolección', 'Warehouse entry by pickup')}
      className="rounded-lg border border-verde/40 bg-white p-4 text-sm"
    >
      <h2 className="font-medium">
        {tr('Fecha de ingreso a bodega por recolección', 'Warehouse entry date by pickup')}
      </h2>
      <form
        className="mt-2 flex flex-wrap items-end gap-3"
        onSubmit={e => {
          e.preventDefault();
          void pedir('revisar');
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs text-apagado">
            {tr('Recolección', 'Pickup')}
            <NotaDeAyuda texto={AYUDA_ENTRADA_A_BODEGA.recoleccion} dentroDeEtiqueta />
          </span>
          <input
            value={recoleccion}
            onChange={e => {
              setRecoleccion(e.target.value);
              setRevision(null);
            }}
            list="recolecciones-entrada"
            required
            placeholder="S-378"
            className="w-36 rounded-md border border-linea px-2 py-1"
          />
          <datalist id="recolecciones-entrada">
            {recolecciones
              .filter(o => o.valor !== 'sin')
              .map(o => (
                <option key={o.valor} value={o.valor} />
              ))}
          </datalist>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-apagado">
            {tr('Ingreso a bodega', 'Warehouse entry')}
            <NotaDeAyuda texto={AYUDA_ENTRADA_A_BODEGA.fecha} dentroDeEtiqueta />
          </span>
          <input
            type="date"
            value={fecha}
            onChange={e => setFecha(e.target.value)}
            className="rounded-md border border-linea px-2 py-1"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-apagado">
            {tr('Aplicar a', 'Apply to')}
            <NotaDeAyuda texto={AYUDA_ENTRADA_A_BODEGA.alcance} dentroDeEtiqueta />
          </span>
          <select
            value={alcance}
            onChange={e => setAlcance(e.target.value as 'vacias' | 'todas')}
            className="rounded-md border border-linea px-2 py-1"
          >
            <option value="vacias">{tr('Solo los que no tienen fecha', 'Only those without a date')}</option>
            <option value="todas">
              {tr('Todos (reemplaza las fechas que tengan)', 'All (replaces existing dates)')}
            </option>
          </select>
        </label>
        <button
          type="submit"
          disabled={accion.isPending}
          className="rounded-md border border-linea bg-white px-3 py-1.5 hover:bg-papel disabled:opacity-50"
        >
          {tr('Revisar', 'Review')}
        </button>
        <button type="button" onClick={onCerrar} className="px-2 py-1.5 text-apagado hover:text-tinta">
          {tr('Cerrar', 'Close')}
        </button>
      </form>

      {vigente && (
        <div className="mt-3 space-y-1">
          <p>
            {tr(
              `${revision.recoleccion}: ${numero(revision.bigBags)} Big Bags, ${numero(revision.sinFecha)} sin fecha de ingreso.`,
              `${revision.recoleccion}: ${numero(revision.bigBags)} Big Bags, ${numero(revision.sinFecha)} without an entry date.`,
            )}
            {revision.fechasActuales.length > 0 && (
              <span className="text-apagado">
                {' '}
                {tr('Hoy tienen:', 'Current dates:')}{' '}
                {revision.fechasActuales.map(f => `${formatoFecha(f.fecha)} (${numero(f.cantidad)})`).join(', ')}
              </span>
            )}
          </p>
          {revision.sugerida && (
            <p className="text-apagado">
              {tr('Descarga del viaje de camión:', 'Truck trip unloading:')} {formatoFecha(revision.sugerida)}
              {fecha !== revision.sugerida && (
                <button
                  type="button"
                  className="ml-2 text-verde hover:underline"
                  onClick={() => setFecha(revision.sugerida!)}
                >
                  {tr('Usar esta fecha', 'Use this date')}
                </button>
              )}
            </p>
          )}
          {aCambiar !== null && (
            <p className="font-medium">
              {aCambiar
                ? tr(
                    `${numero(aCambiar)} Big Bags quedarán con ingreso a bodega el ${formatoFecha(fecha)}.`,
                    `${numero(aCambiar)} Big Bags will get warehouse entry on ${formatoFecha(fecha)}.`,
                  )
                : tr('No hay Big Bags para cambiar.', 'There are no Big Bags to change.')}
            </p>
          )}
          {aCambiar === null && fecha && revision.modo === 'revisar' && (
            <p className="text-apagado">
              {tr('Pulsa Revisar para ver cuántos cambian.', 'Press Review to see how many change.')}
            </p>
          )}
          {aCambiar !== null && aCambiar > 0 && (
            <button
              type="button"
              disabled={accion.isPending}
              onClick={() => void pedir('aplicar')}
              className="rounded-md bg-verde px-3 py-1.5 font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {tr('Aplicar', 'Apply')}
            </button>
          )}
        </div>
      )}
      {mensaje && (
        <p className={`mt-2 ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`} role="status">
          {mensaje.texto}
        </p>
      )}
    </div>
  );
}
